<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import { MoreVertical, Trash2, Link } from 'lucide-svelte'
  import { notifyImageResized } from '$lib/stores/imageResizeStore'

  interface Props {
    imagePath: string
    onResizeComplete?: () => void
    onDelete?: () => void
  }

  let { imagePath, onResizeComplete, onDelete }: Props = $props()

  let showMenu = $state(false)
  let buttonElement: HTMLButtonElement | null = null
  let isResizing = $state(false)
  let imageMetadata = $state<{ width: number; height: number; size?: number } | null>(null)
  let displayScale = $state<number | null>(null)
  let isUrlImage = $state(false) // Track if image is from URL
  let imageLoadFailed = $state(false) // Track if image failed to load
  let isElectron = $state(false) // Track if running in Electron

  const sizeOptions = [
    { label: '크게', size: 80, percent: '80%' },
    { label: '중간', size: 60, percent: '60%' },
    { label: '작게', size: 40, percent: '40%' }
  ]

  function formatFileSize(bytes?: number): string {
    if (!bytes) return ''

    if (bytes < 1024) {
      return `${bytes} B`
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }
  }

  async function loadImageMetadata() {
    try {
      // Check if imagePath is a URL (http:// or https://)
      isUrlImage = imagePath.startsWith('http://') || imagePath.startsWith('https://')

      if (isUrlImage) {
        // For URLs, use browser's natural dimensions from the img element
        await loadMetadataFromImage()
        return
      }

      const isElectron = typeof window !== 'undefined' && (window as any).electronAPI

      if (!isElectron || !(window as any).electronAPI.getImageMetadata) {
        // Fallback to browser API
        await loadMetadataFromImage()
        return
      }

      const result = await (window as any).electronAPI.getImageMetadata(imagePath)

      if (result.success) {
        imageMetadata = {
          width: result.width,
          height: result.height,
          size: result.size
        }

        // Calculate display scale
        calculateDisplayScale()
      } else {
        // Fallback to browser API if file not found
        await loadMetadataFromImage()
      }
    } catch (error) {
      console.error('[ImageSizeMenu] Failed to load image metadata:', error)
      // Last resort fallback
      await loadMetadataFromImage()
    }
  }

  async function loadMetadataFromImage() {
    try {
      // Find the img element in the parent wrapper
      const menuElement = buttonElement?.closest('.image-size-menu')
      const wrapperElement = menuElement?.closest('.image-wrapper')
      const imgElement = wrapperElement?.querySelector('img') as HTMLImageElement

      if (!imgElement) {
        imageLoadFailed = true
        return
      }

      // Wait for image to load if not loaded yet
      if (!imgElement.complete) {
        await new Promise((resolve, reject) => {
          imgElement.onload = resolve
          imgElement.onerror = reject
          // Timeout after 5 seconds
          setTimeout(reject, 5000)
        })
      }

      // Use natural dimensions from the loaded image
      if (imgElement.naturalWidth > 0 && imgElement.naturalHeight > 0) {
        imageMetadata = {
          width: imgElement.naturalWidth,
          height: imgElement.naturalHeight,
          size: undefined // Size not available for URLs
        }
        imageLoadFailed = false

        // Calculate display scale
        calculateDisplayScale()
      } else {
        // Image loaded but has no dimensions (broken image)
        imageLoadFailed = true
      }
    } catch (error) {
      // In web environment, silently fail for local file paths
      const isElectron = typeof window !== 'undefined' && (window as any).electronAPI
      if (isElectron) {
        console.error('[ImageSizeMenu] Failed to load metadata from image element:', error)
      }
      imageLoadFailed = true
    }
  }

  function calculateDisplayScale() {
    if (!imageMetadata) return

    // Find the img element in the parent wrapper
    const menuElement = buttonElement?.closest('.image-size-menu')
    const wrapperElement = menuElement?.closest('.image-wrapper')
    const imgElement = wrapperElement?.querySelector('img')

    if (!imgElement) return

    // Get actual displayed dimensions
    const displayedWidth = imgElement.clientWidth
    const displayedHeight = imgElement.clientHeight

    // Calculate scale based on width (or height, they should be proportional)
    if (imageMetadata.width > 0) {
      const scale = (displayedWidth / imageMetadata.width) * 100
      displayScale = Math.round(scale)
    }
  }

  function toggleMenu(e: Event) {
    e.stopPropagation()
    showMenu = !showMenu

    // Recalculate display scale when menu opens
    if (showMenu) {
      calculateDisplayScale()
    }
  }

  async function selectSize(sizePercent: number, e: Event) {
    e.stopPropagation()
    showMenu = false

    if (isResizing) return

    isResizing = true

    try {
      const isElectron = typeof window !== 'undefined' && (window as any).electronAPI

      if (!isElectron || !( window as any).electronAPI.resizeImage) {
        console.error('Electron API not available')
        return
      }

      // Mark as self-change to prevent FileSystemWatcher from triggering
      const fsWatcher = (window as any).__fileSystemWatcher
      if (fsWatcher && typeof fsWatcher.markSelfChange === 'function') {
        // Mark with .md extension for consistency with watcher
        const filePathForWatcher = imagePath.endsWith('.md') ? imagePath : imagePath
        fsWatcher.markSelfChange(filePathForWatcher)
      }

      const result = await (window as any).electronAPI.resizeImage({
        imagePath,
        sizePercent
      })

      if (result.success) {
        // Reload image metadata after resize
        await loadImageMetadata()

        // Trigger reload/refresh of the image
        onResizeComplete?.()

        // Notify all editors to refresh this image via store
        notifyImageResized(imagePath)

        // Show success message
        window.dispatchEvent(new CustomEvent('status-message', {
          detail: {
            message: `이미지가 ${sizePercent}%로 리사이즈되었습니다`,
            type: 'success'
          }
        }))
      } else {
        console.error('Failed to resize image:', result.error)
        window.dispatchEvent(new CustomEvent('status-message', {
          detail: {
            message: '이미지 리사이즈에 실패했습니다',
            type: 'error'
          }
        }))
      }
    } catch (error) {
      console.error('Error resizing image:', error)
      window.dispatchEvent(new CustomEvent('status-message', {
        detail: {
          message: '이미지 리사이즈 중 오류가 발생했습니다',
          type: 'error'
        }
      }))
    } finally {
      isResizing = false
    }
  }

  function handleDelete(e: Event) {
    e.stopPropagation()
    onDelete?.()
  }

  function handleOpenLink(e: Event) {
    e.stopPropagation()
    if (isUrlImage) {
      window.open(imagePath, '_blank', 'noopener,noreferrer')
    }
  }

  function handleClickOutside(e: MouseEvent) {
    if (buttonElement && !buttonElement.contains(e.target as Node)) {
      showMenu = false
    }
  }

  onMount(() => {
    // Check if running in Electron
    isElectron = typeof window !== 'undefined' && !!(window as any).electronAPI

    document.addEventListener('click', handleClickOutside)
    loadImageMetadata()
  })

  onDestroy(() => {
    document.removeEventListener('click', handleClickOutside)
  })
</script>

<div class="image-size-menu" class:image-load-failed={imageLoadFailed}>
  <button
    bind:this={buttonElement}
    class="menu-button"
    class:disabled={imageLoadFailed}
    onclick={toggleMenu}
    aria-label="이미지 크기 조정"
    title={imageLoadFailed ? "이미지를 찾을 수 없습니다" : "이미지 크기 조정"}
    disabled={imageLoadFailed}
  >
    <MoreVertical size={14} />
  </button>

  {#if isUrlImage}
    <button
      class="menu-button link-button"
      onclick={handleOpenLink}
      aria-label="새 탭에서 열기"
      title={imagePath}
    >
      <Link size={14} />
    </button>
  {/if}

  {#if onDelete}
    <button
      class="menu-button delete-button"
      onclick={handleDelete}
      aria-label="이미지 삭제"
      title="이미지 삽입 제거"
    >
      <Trash2 size={14} />
    </button>
  {/if}

  {#if showMenu}
    <div class="menu-dropdown">
      {#if imageMetadata}
        <div class="info-item">
          <span class="label">실제 크기</span>
          <span class="value">{imageMetadata.width} × {imageMetadata.height}</span>
        </div>
        {#if displayScale !== null}
          <div class="info-item">
            <span class="label">현재 크기</span>
            <span class="value">{displayScale}% {#if imageMetadata.size}<span class="file-size">({formatFileSize(imageMetadata.size)})</span>{/if}</span>
          </div>
        {/if}
        <div class="menu-divider"></div>
      {/if}
      {#if isElectron}
        <div class="section-title">이미지 변환</div>
        {#each sizeOptions as option}
          <button
            class="menu-item"
            onclick={(e) => selectSize(option.size, e)}
            disabled={isResizing || isUrlImage}
          >
            <span class="label">{option.label}</span>
            <span class="percent">{option.percent}</span>
          </button>
        {/each}
      {:else}
        <div class="section-title web-only-message">이미지 리사이징은 데스크톱 앱에서만 사용 가능합니다</div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .image-size-menu {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 10;
    line-height: normal;
    display: flex;
    gap: 4px;
  }

  .menu-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    opacity: 0;
    transition: all 0.2s ease;
    color: white;
    backdrop-filter: blur(8px);
  }

  .menu-button:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.05);
  }

  .menu-button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .image-load-failed .menu-button {
    opacity: 0.3;
  }

  .link-button:hover {
    background: rgba(59, 130, 246, 0.85);
    color: white;
  }

  .delete-button:hover {
    background: rgba(239, 68, 68, 0.85);
    color: white;
  }

  .image-size-menu:hover .menu-button {
    opacity: 1;
  }

  /* Dark theme adjustments */
  :global(.dark-theme) .menu-button {
    background: rgba(255, 255, 255, 0.15);
    color: white;
  }

  :global(.dark-theme) .menu-button:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  :global(.dark-theme) .link-button:hover {
    background: rgba(59, 130, 246, 0.85);
  }

  :global(.dark-theme) .delete-button:hover {
    background: rgba(239, 68, 68, 0.85);
  }

  /* Auto theme: follow system preference */
  @media (prefers-color-scheme: dark) {
    :root:not(.light-theme) .menu-button {
      background: rgba(255, 255, 255, 0.15);
      color: white;
    }

    :root:not(.light-theme) .menu-button:hover {
      background: rgba(255, 255, 255, 0.25);
    }

    :root:not(.light-theme) .link-button:hover {
      background: rgba(59, 130, 246, 0.85);
    }

    :root:not(.light-theme) .delete-button:hover {
      background: rgba(239, 68, 68, 0.85);
    }
  }

  .menu-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    min-width: 180px;
    background: var(--bg-primary);
    border: 1px solid var(--border-primary);
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    z-index: 1000;
    white-space: nowrap;
  }

  .info-item {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 6px 14px;
  }

  .info-item .label {
    font-size: 11px;
    font-weight: 400;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  .info-item .value {
    font-size: 10px;
    color: var(--text-tertiary);
    margin-left: 12px;
    font-family: 'SF Mono', 'Monaco', 'Cascadia Code', monospace;
    line-height: 1.4;
  }

  .info-item .file-size {
    font-size: 9px;
    opacity: 0.85;
  }

  .menu-divider {
    height: 1px;
    background: var(--border-primary);
    margin: 2px 0;
  }

  .section-title {
    padding: 8px 14px 6px;
    font-size: var(--ui-font-size-xs, 11px);
    font-weight: 500;
    color: var(--text-tertiary);
    text-align: center;
  }

  .web-only-message {
    padding: 12px 14px;
    font-size: var(--ui-font-size-xs, 11px);
    line-height: 1.4;
    opacity: 0.7;
  }

  .menu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 14px;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: background 0.15s ease;
    font-size: var(--ui-font-size-sm, 13px);
  }

  .menu-item:hover {
    background: var(--bg-secondary);
  }

  .menu-item:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .menu-item:disabled:hover {
    background: transparent;
  }

  .label {
    font-weight: 500;
    color: var(--text-primary);
  }

  .percent {
    font-size: var(--ui-font-size-xs, 11px);
    color: var(--text-tertiary);
    margin-left: 12px;
  }
</style>
